import {useEffect, useRef} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {useQuery} from '@apollo/client'
import {
  setStates as setStatesAction,
  setFilterList as setFilterListAction,
  setUseCaseList as setUseCaseListAction,
  setPersonaList as setPersonaListAction,
  setStateListFilter as setStateListFilterAction,
  setPointOfInterestFilter as setPointOfInterestFilterAction,
  setFilterListData as setFilterListDataAction,
  selectStates,
  selectFilterList,
  selectUseCaseList,
  selectPersonaList,
  selectStateListFilter,
  selectPointOfInterestFilter,
  selectFilterListData
} from '../slices/filterListDetailSlice'
import {GET_FILTER_DETAILS_LIST} from '../../api/queries/queryFilterListDetail'
import {setFilterDetailsLoading} from '../slices/breadCrumbsSlice'
import {
  setAvailableFilters,
  setCustomizations,
  setOutreachPermissions,
  selectAvailableFilters
} from '../slices/filterManagementSlice'
import {selectPermission} from '../slices/permissionSlice'

// Default configuration object defined outside to avoid creating new object on each call
const DEFAULT_STATES_CONFIG = {entireUnitedStateLabel: 'Entire US', totalStates: 51}

/**
 * Transform state list with optional "Entire US" entry
 */
const transformStateListData = (stateList, statesConfig) => {
  if (!statesConfig.entireUnitedStateLabel || !stateList) {
    return stateList ? [...stateList].map(({STATE_NAME: title, ...rest}) => ({title, ...rest})) : []
  }

  const stateListFilter = stateList.length === statesConfig.totalStates ? [...stateList] : [...(stateList || [])]

  if (stateList.length === statesConfig.totalStates) {
    stateListFilter.unshift({STATE_ID: 0, STATE_NAME: statesConfig.entireUnitedStateLabel, IS_ACTIVE: true})
  }

  return stateListFilter.map(({STATE_NAME: title, ...rest}) => ({title, ...rest}))
}

/**
 * Transform filter list data
 */
const transformFilterListData = (filterList) =>
  filterList?.map((filter) => ({
    title: filter.FILTER_NAME,
    FILTER_ID: filter.FILTER_ID,
    FILTER_CATEGORY_ID: filter.FILTER_CATEGORY_ID,
    FILTER_KEY: filter.FILTER_KEY,
    FILTER_CUSTOM_NAME: filter.FILTER_CUSTOM_NAME,
    IS_ACTIVE: filter.IS_ACTIVE
  }))

/**
 * Extract filter custom names from a data array
 */
const extractFilterCustomNames = (dataArray, customNames) => {
  if (!dataArray) return
  for (const filter of dataArray) {
    if (filter.FILTER_CUSTOM_NAME) {
      customNames[filter.FILTER_KEY] = filter.FILTER_CUSTOM_NAME
    }
  }
}

/**
 * Extract category custom names from category data array
 */
const extractCategoryCustomNames = (categoryArray, categoryNames) => {
  if (!categoryArray) return
  for (const categoryData of categoryArray) {
    if (categoryData.FILTER_CATEGORY_CUSTOM_NAME && categoryData.FILTER_CATEGORY_ID) {
      categoryNames[categoryData.FILTER_CATEGORY_ID] = categoryData.FILTER_CATEGORY_CUSTOM_NAME
    }
  }
}

/**
 * Build customizations object from filter details data
 */
const buildCustomizations = (filterDetailsList) => {
  const customizations = {filterNames: {}, categoryNames: {}}

  // Extract from filter list
  extractFilterCustomNames(filterDetailsList?.filterList?.data, customizations.filterNames)
  extractCategoryCustomNames(filterDetailsList?.filterList?.filter_category_name, customizations.categoryNames)

  // Extract from SDOH list
  extractFilterCustomNames(filterDetailsList?.sdohList?.data, customizations.filterNames)
  extractCategoryCustomNames(filterDetailsList?.sdohList?.filter_category_name, customizations.categoryNames)

  return customizations
}

// Hook that performs the GraphQL query - should be called ONCE at app level
export const useFilterListDetailQuery = (statesConfig = DEFAULT_STATES_CONFIG) => {
  const dispatch = useDispatch()
  const hasProcessedData = useRef(false)
  const availableFilters = useSelector(selectAvailableFilters)

  // Call useQuery directly in the hook instead of through a function
  const {
    loading: filterDetailsLoading,
    error: filterDetailsError,
    data,
    refetch: refetchFilterDetailsList
  } = useQuery(GET_FILTER_DETAILS_LIST)

  // Sync loading state to Redux whenever it changes
  useEffect(() => {
    dispatch(setFilterDetailsLoading(filterDetailsLoading))
  }, [filterDetailsLoading, dispatch])

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!filterDetailsLoading && data?.filterDetailsList && !hasProcessedData.current) {
      hasProcessedData.current = true
      const filterDetails = data.filterDetailsList

      // Transform data using helper functions
      const stateListFilter = transformStateListData(filterDetails?.stateList, statesConfig)
      const filterListData = transformFilterListData(filterDetails?.filterList?.data)

      const pointOfInterestFilter = filterDetails?.pointOfInterestList?.map(({POI_NAME: title, ...rest}) => ({
        title,
        ...rest
      }))

      // Extract customizations and available filters
      const customizations = buildCustomizations(filterDetails)
      const availableFilterKeys =
        filterListData?.filter((filter) => filter.IS_ACTIVE !== false).map((filter) => filter.FILTER_KEY) || []

      // Dispatch to filterManagement slice
      dispatch(setCustomizations(customizations))
      dispatch(setAvailableFilters([...availableFilters, ...availableFilterKeys]))

      // Dispatch transformed filter values
      dispatch(setStateListFilterAction(stateListFilter || []))
      dispatch(setPointOfInterestFilterAction(pointOfInterestFilter || []))
      dispatch(setFilterListDataAction(filterListData || []))

      // Dispatch raw data values
      dispatch(setStatesAction({stateList: (filterDetails?.stateList || []).filter((item) => item.STATE_ID !== 0)}))
      dispatch(
        setFilterListAction({
          data: filterDetails?.filterList?.data || [],
          filterCategoryNames: filterDetails?.filterList?.filter_category_name || []
        })
      )
      dispatch(setUseCaseListAction({useCaseList: filterDetails?.usecaseList || []}))
      dispatch(setPersonaListAction({personaList: filterDetails?.personaList || []}))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterDetailsLoading, data, dispatch, statesConfig])

  return {
    filterDetailsLoading,
    filterDetailsError,
    refetchFilterDetailsList
  }
}

// Hook that just reads from Redux - can be called in any component
export const useFilterListDetail = () => {
  const states = useSelector(selectStates)
  const filterList = useSelector(selectFilterList)
  const useCaseList = useSelector(selectUseCaseList)
  const personaList = useSelector(selectPersonaList)
  const stateListFilter = useSelector(selectStateListFilter)
  const pointOfInterestFilter = useSelector(selectPointOfInterestFilter)
  const filterListData = useSelector(selectFilterListData)

  return {
    states,
    filterList,
    useCaseList,
    personaList,
    stateListFilter,
    pointOfInterestFilter,
    filterListData
  }
}

/**
 * Hook to integrate outreach permissions into filter management
 * Should be called at app level after both filterDetails and permissions are loaded
 *
 * This hook:
 * 1. Reads user permissions for benefits/workforce
 * 2. Conditionally adds outreach filters to availableFilters if user has any permissions
 * 3. Stores the permission arrays for UI to disable individual options
 */
export const useOutreachPermissions = () => {
  const dispatch = useDispatch()
  const permission = useSelector(selectPermission)
  const availableFilters = useSelector(selectAvailableFilters)
  const hasProcessedPermissions = useRef(false)

  useEffect(() => {
    // Only process once when permissions are available
    if (!permission?.healthEquityConditionList || hasProcessedPermissions.current) {
      return
    }

    const benefitsPermissions = permission.healthEquityConditionList?.benefitsEligibilityPermissions || []
    const workforcePermissions = permission.healthEquityConditionList?.workforceRecruitmentPermissions || []

    // Store outreach permissions in Redux for UI to use
    dispatch(
      setOutreachPermissions({
        benefitsEligibility: benefitsPermissions,
        workforceRecruitment: workforcePermissions
      })
    )

    // Build list of outreach filters to add based on permissions
    const outreachFiltersToAdd = []
    // Add receivingBenefits if user has any benefits permission
    if (benefitsPermissions.length > 0 && !availableFilters.includes('receivingBenefits')) {
      outreachFiltersToAdd.push('receivingBenefits')
    }

    // Add workforceRecruitment if user has any workforce permission
    if (workforcePermissions.length > 0 && !availableFilters.includes('workforceRecruitment')) {
      outreachFiltersToAdd.push('workforceRecruitment')
    }
    // Add outreach filters to available filters
    if (outreachFiltersToAdd.length > 0) {
      dispatch(setAvailableFilters([...availableFilters, ...outreachFiltersToAdd]))
    }

    hasProcessedPermissions.current = true
  }, [permission, availableFilters, dispatch])
}
